import cn from 'classnames';
import { environmentVariables } from '#global/styles/environment-variables.json';
import { useMatchMedia } from '#hooks/useMatchMedia.ts';
import styles from './PageHeader.module.css';

type Props = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  preventTitleWrap?: boolean;
  wrapAtPx?: number;
  /**
   * Controls when the header lays its children out as a column instead of a row. Defaults to
   * stacking below `--size-breakpoint-xs` (or `wrapAtPx`); pass a value when the page knows better —
   * e.g. its controls are too wide for that default (see HostsPage). The same boolean drives the
   * column layout and the subtitle placement, so the two can't disagree.
   */
  stacked?: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

export function PageHeader({
  title,
  subtitle,
  preventTitleWrap,
  children,
  className,
  titleClassName,
  subtitleClassName,
  wrapAtPx,
  stacked: stackedProp,
  ref,
}: Props) {
  // wrapAtPx is a bare number — it needs its unit appended, or the media query is invalid and
  // matchMedia returns "not all", which never matches at any width.
  const wrapBelow = wrapAtPx != null ? `${wrapAtPx}px` : environmentVariables['--size-breakpoint-xs'];
  const isExtraNarrow = useMatchMedia(`(max-width: ${wrapBelow})`);
  const stacked = stackedProp ?? isExtraNarrow;

  return (
    <div ref={ref} className={styles.PageHeader}>
      <div className={cn(styles.PageHeader_main, { [styles.PageHeader_main___stacked]: stacked }, className)}>
        <h1
          className={cn(
            titleClassName,
            styles.PageHeader_title,
            { [styles.PageHeader_title___preventWrap]: preventTitleWrap },
            'h2 mb-0'
          )}
        >
          {title}
        </h1>
        {stacked && subtitle ?
          <div className={cn('mb-24', subtitleClassName, styles.PageHeader_subtitle)} key="subtitle">
            {subtitle}
          </div>
        : null}
        {children}
      </div>
      {!stacked && subtitle ?
        <div className={cn(subtitleClassName, styles.PageHeader_subtitle)} key="subtitle">
          {subtitle}
        </div>
      : null}
    </div>
  );
}
